/**
 * UpgradeTeaser — Blurred "see what you're missing" premium gate
 * Shows content blurred with a gradient overlay + upgrade CTA
 */
import React, { useState } from 'react';
import { Lock, Crown, Zap, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ComparePlans from './ComparePlans';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

const TIER_COLORS = {
  starter:   { primary: '#00d4ff', label: 'Pro',    price: '$29/mo',  trial: '7-day free trial', icon: Crown },
  pro:       { primary: '#00d4ff', label: 'Pro',    price: '$29/mo',  trial: '7-day free trial', icon: Crown },
  elite:     { primary: '#ffa502', label: 'Elite',  price: '$79/mo',  trial: null,               icon: Zap },
  unlimited: { primary: '#ffa502', label: 'Elite',  price: '$79/mo',  trial: null,               icon: Zap },
};

export default function UpgradeTeaser({
  tier = 'starter',
  feature,
  description,
  children,
  onSignIn,
  blurAmount = 'sm',
  gradientFrom = 'transparent',
  className = '',
}) {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showComparePlans, setShowComparePlans] = useState(false);
  const config = TIER_COLORS[tier] || TIER_COLORS.starter;
  const TierIcon = config.icon;

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      // Trigger auth modal via custom event (picked up by Sidebar)
      window.dispatchEvent(new CustomEvent('profitspot:open-auth'));
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('profitspot_token');
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: tier, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast("Couldn’t start checkout. Please try again.");
    } catch {
      toast("Couldn’t start checkout. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className={`relative overflow-hidden rounded-xl ${className}`} data-testid={`upgrade-teaser-${tier}`}>
      {/* Blurred preview content */}
      <div className={`blur-${blurAmount} pointer-events-none select-none opacity-60`} aria-hidden="true">
        {children}
      </div>

      {/* Bottom gradient fade */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, transparent 0%, ${gradientFrom === 'transparent' ? 'rgba(10,10,18,0.7)' : gradientFrom} 40%, rgba(10,10,18,0.97) 100%)`,
        }}
      />

      {/* CTA overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 py-8">
        <div
          className="w-full max-w-sm bg-[#0f0f1a]/95 border rounded-2xl p-6 text-center shadow-2xl"
          style={{ borderColor: `${config.primary}30` }}
          data-testid={`upgrade-cta-${tier}`}
        >
          {/* Icon + badge */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${config.primary}15`, border: `1px solid ${config.primary}30` }}
          >
            <TierIcon className="w-6 h-6" style={{ color: config.primary }} />
          </div>

          {/* Lock label */}
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <Lock className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-500 uppercase tracking-wider">
              {config.label} Feature
            </span>
          </div>

          {feature && <p className="text-white font-bold text-sm mb-1">{feature}</p>}
          {description && <p className="text-gray-400 text-xs mb-4 leading-relaxed">{description}</p>}

          {/* CTA button */}
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-2.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50 hover:brightness-110"
            style={{ backgroundColor: config.primary, color: config.primary === '#ffa502' ? '#000' : '#000' }}
            data-testid={`upgrade-btn-${tier}`}
          >
            {loading ? 'Loading...' : config.trial
              ? (isAuthenticated ? `Start 7-day trial` : `Sign in to start 7-day trial`)
              : (isAuthenticated ? `Upgrade to ${config.label} — ${config.price}` : `Sign in to upgrade`)}
          </button>

          {config.trial && (
            <p className="text-[10px] text-gray-500 mt-2">
              {`Then ${config.price} — cancel anytime`}
            </p>
          )}

          {/* Compare plans link */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowComparePlans(true); }}
            className="text-[11px] text-gray-500 hover:text-gray-300 mt-3 transition-colors underline underline-offset-2"
            data-testid="compare-plans-link"
          >
            Compare all plans →
          </button>
        </div>
      </div>

      {/* Compare Plans Modal */}
      <ComparePlans open={showComparePlans} onClose={() => setShowComparePlans(false)} />
    </div>
  );
}
